/**
 * useCars Hook
 * Fetches and manages car listings with SWR
 */

import { deduplicateTwhOffers, mapVehicleToCar } from '@/lib/mappers'
import { formatDateForApi } from '@/lib/utils'
import { tattersallService } from '@/services/tattersall'
import type { Car, CarFilters, PaginatedResponse } from '@/types'
import { SearchRequestModel } from '@/types/api-models'
import useSWR from 'swr'

interface UseCarsOptions {
	filters?: CarFilters
	searchParams?: Partial<SearchRequestModel>
	page?: number
	limit?: number
}

export function useCars(options: UseCarsOptions = {}) {
	const { filters, searchParams, page = 1, limit = 12 } = options

	// Default search params
	const defaultSearchParams: SearchRequestModel = {
		pickupStationId: 'BOGT01', // Default station ID
		dropoffStationId: 'BOGT01',
		pickupDate: formatDateForApi(new Date()),
		dropoffDate: formatDateForApi(new Date(Date.now() + 86400000)),
		driverAge: 25,
		countryCode: 'CO',
		vehicleType: 'coche',
		filterPriceOrder: 'pricelow',
		filterPriceMinValue: 0,
		filterPriceMaxValue: 100000000,
		filterSeatsValue: 2,
		filterTransmissionValue: 'both',
		...searchParams,
	} as SearchRequestModel

	const fetcher = async () => {
		if (
			!defaultSearchParams.pickupStationId ||
			!defaultSearchParams.dropoffStationId
		) {
			return { data: [], pagination: { page, limit, total: 0, totalPages: 0 } }
		}

		const vehicles = await tattersallService.searchVehicles(defaultSearchParams)

		// Deduplicar variantes TWH: el mismo vehículo no aparece dos veces
		const deduplicated = deduplicateTwhOffers(vehicles || [])
		const cars = deduplicated.map(mapVehicleToCar)

		// Client-side filtering
		const filteredCars = cars.filter(car => {
			// Category filter
			if (filters?.category?.length) {
				if (!filters.category.includes(car.category)) return false
			}

			// Transmission filter
			if (filters?.transmission?.length) {
				if (!filters.transmission.includes(car.specifications.transmission))
					return false
			}

			// Fuel Type filter
			if (filters?.fuelType?.length) {
				if (!filters.fuelType.includes(car.specifications.fuelType))
					return false
			}

			// Seats filter
			if (filters?.seats?.length) {
				const carSeats = car.specifications.seats
				const matchesSeats = filters.seats.some(seat => {
					if (seat >= 9) return carSeats >= 9
					return carSeats === seat
				})
				if (!matchesSeats) return false
			}

			return true
		})

		return {
			data: filteredCars,
			pagination: {
				page,
				limit,
				total: filteredCars.length,
				totalPages: Math.ceil(filteredCars.length / limit),
			},
		}
	}

	const { data, error, isLoading, mutate } = useSWR<PaginatedResponse<Car>>(
		['/cars', defaultSearchParams, filters],
		fetcher,
		{
			revalidateOnFocus: false,
			dedupingInterval: 60000, // 1 minute
		},
	)

	return {
		cars: data?.data || [],
		pagination: data?.pagination,
		isLoading,
		isError: error,
		mutate,
	}
}
